import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Label,
  Spinner,
  Card,
  CardBody,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { updateProfile } from "../../../../actions/customer";
import { CustomSnackbar } from "../../../../helpers/CustomSnackbar";
import {
  authKey_local_storage_key,
  getDropdownOptions,
  getSettingByKey,
  getWorkExpText,
  toArray,
} from "../../../../helpers/common";
import SearchableDropdown from "../../../../helpers/Common/SearchableDropdown";
import { getJobIndustries, getCategories } from "../../../../api/categories";
import { getFrontendSettings } from "../../../../api/settings";
import { getCustomerDetails } from "../../../../actions/customer";

const CompulsaryCandidateProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { profile } = useSelector((state) => state.customer);
  const jobLocations = useSelector((state) => state.jobLocations.data) || [];
  const settings = useSelector((state) => state.settings.data);

  const workExperienceList = toArray(
    getSettingByKey(settings, "total_work_experiences")?.value,
    "\n",
  );

  const [loading, setLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [salaryOptions, setSalaryOptions] = useState([]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    date_of_birth: "",
    gender: "",
    designation: "",
    location: "",
    total_work_experience: "",
    resumeFile: "",
    currentSalaryStr: "",
    expectedSalaryStr: "",
    jobInduestry: "",
  });

  useEffect(() => {
    if (profile && profile.data) {
      const profileData = profile.data;
      setFormData((prev) => ({
        ...prev,
        firstName: profileData.firstName || "",
        lastName: profileData.lastName || "",
      }));
    }
  }, [profile]);

  useEffect(() => {
    setApiLoading(true);
    getCategories()
      .then((res) => {
        setCategories(res?.data?.data || []);
      })
      .catch((err) => {
        console.error("Categories fetch error:", err);
      })
      .finally(() => setApiLoading(false));

    getJobIndustries()
      .then((res) => {
        const parsedTypes = Array.isArray(res)
          ? res
          : res?.data?.data || res?.data || [];
        setJobTypes(parsedTypes);
      })
      .catch((err) => {
        console.error("Error fetching job industries:", err);
      });

    // 🎯 fetch salary options for the Expected Salary dropdown
    getFrontendSettings()
      .then((response) => {
        const rawData = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        const salarySetting = rawData.find(
          (item) => item.key === "salary_range",
        );
        if (salarySetting && salarySetting.value) {
          const parsedSalary = salarySetting.value
            .split(/\r?\n/)
            .map((opt) => opt.trim())
            .filter((opt) => opt.length > 0);
          setSalaryOptions(parsedSalary);
        }
      })
      .catch((error) => {
        console.error("Error fetching frontend settings:", error);
      });
  }, []);

  const onValueChange = (e, name) => {
    const value = e?.target ? e.target.value : e;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 🎯 number-only input handler, used only for Current Salary now
  const onNumberInputChange = (e, name) => {
    const inputValue = e.target.value;

    if (!inputValue) {
      setFormData((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    if (!/^\d+$/.test(inputValue)) return;

    setFormData((prev) => ({ ...prev, [name]: inputValue }));
  };

  const handleResumeUpload = async (file) => {
    try {
      setLoading(true);

      const formPayload = new FormData();
      formPayload.append("file", file);

      const token = localStorage.getItem(authKey_local_storage_key);

      const res = await axios.post(
        "https://sdsjobseekers.runasp.net/api/CandidateAttachment/UploadResume",
        formPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res?.status === 200) {
        const uploadedFileName =
          res?.data?.fileName || res?.data?.data?.fileName || res?.data?.name;

        if (uploadedFileName) {
          setFormData((prev) => ({
            ...prev,
            resumeFile: uploadedFileName,
          }));
        }

        CustomSnackbar.success("Resume uploaded successfully");
        return uploadedFileName;
      }
    } catch (err) {
      console.error("Resume upload failed:", err);
      CustomSnackbar.error(
        err?.response?.data?.message || "Resume upload failed",
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const onFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    await handleResumeUpload(file);
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!formData.firstName) {
      CustomSnackbar.error("Please enter your First Name");
      return;
    }
    if (!formData.lastName) {
      CustomSnackbar.error("Please enter your Last Name");
      return;
    }
    if (!formData.date_of_birth) {
      CustomSnackbar.error("Please enter your date of birth");
      return;
    }
    if (!formData.gender) {
      CustomSnackbar.error("Please select your gender");
      return;
    }
    if (!formData.designation) {
      CustomSnackbar.error("Please enter your designation");
      return;
    }
    if (!formData.location) {
      CustomSnackbar.error("Please select your location");
      return;
    }
    if (!formData.total_work_experience) {
      CustomSnackbar.error("Please select your work experience");
      return;
    }
    if (!formData.resumeFile) {
      CustomSnackbar.error("Please upload your resume, it's compulsory");
      return;
    }
    if (!formData.currentSalaryStr) {
      CustomSnackbar.error("Please enter your current salary");
      return;
    }
    if (!formData.expectedSalaryStr) {
      CustomSnackbar.error("Please select your expected salary");
      return;
    }

    setLoading(true);

    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      dateOfBirth: formData.date_of_birth,
      gender: formData.gender,
      designation: formData.designation,
      location: formData.location,
      totalWorkExperience: formData.total_work_experience,
      resumeFile: formData.resumeFile,
      currentSalaryStr: formData.currentSalaryStr,
      expectedSalaryStr: formData.expectedSalaryStr,
      jobInduestry: formData.jobInduestry || "",
      isProfileComplete: true,
    };

    dispatch(updateProfile({ ...payload, id: profile.id }))
      .then((res) => {
        if (res && (res.status === 200 || res.success)) {
          CustomSnackbar.success("Profile completed successfully!");

          dispatch(getCustomerDetails(profile.id))
            .then(() => {
              setLoading(false);
              navigate("/candidate/profile/update");
            })
            .catch(() => {
              setLoading(false);
              navigate("/candidate/profile/update");
            });
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("Profile completion error:", err);
        setLoading(false);
      });
  };

  const maxDateAllowed = new Date(
    new Date().setFullYear(new Date().getFullYear() - 18),
  )
    .toISOString()
    .split("T")[0];
const isFormValid =
  formData.firstName.trim() &&
  formData.lastName.trim() &&
  formData.date_of_birth &&
  formData.gender &&
  formData.designation &&
  formData.location &&
  formData.total_work_experience &&
  formData.resumeFile &&
  formData.currentSalaryStr &&
  formData.expectedSalaryStr;
  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{ minHeight: "100vh", background: "#f4f6f9", padding: "24px" }}
    >
      <div style={{ width: "100%", maxWidth: "640px" }}>
        <div className="text-center mb-4 mt-5">
          <h4 className="fw-bold mb-2">Complete Your Basic Profile</h4>
          <p className="text-muted mb-0">
            Please fill in a few essential details first — you'll get access
            to your dashboard and the rest of the site right after.
          </p>
        </div>

        <Card className="border-0 shadow-sm rounded-4">
          <CardBody className="p-4">
            <Form onSubmit={onSubmit}>
              <Row>
                <Col lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="firstName" className="form-label">
                      First Name *
                    </Label>
                    <Input
                      type="text"
                      id="firstName"
                      placeholder="Enter your First name"
                      value={formData.firstName}
                      onChange={(e) => onValueChange(e, "firstName")}
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="lastName" className="form-label">
                      Last Name *
                    </Label>
                    <Input
                      type="text"
                      id="lastName"
                      placeholder="Enter your Last name"
                      value={formData.lastName}
                      onChange={(e) => onValueChange(e, "lastName")}
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="date_of_birth" className="form-label">
                      Date of Birth *
                    </Label>
                    <Input
                      type="date"
                      id="date_of_birth"
                      value={formData.date_of_birth}
                      onChange={(e) => onValueChange(e, "date_of_birth")}
                      max={maxDateAllowed}
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="gender" className="form-label">
                      Gender *
                    </Label>
                    <SearchableDropdown
                      placeholder="Select your gender"
                      options={[
                        { label: "Male", value: "Male" },
                        { label: "Female", value: "Female" },
                        { label: "Transgender", value: "Transgender" },
                      ]}
                      value={formData.gender}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          gender: value?.toString(),
                        }))
                      }
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="jobtitle" className="form-label">
                      Job Title / Designation *
                    </Label>
                    <SearchableDropdown
                      options={getDropdownOptions(categories, {
                        label: "name",
                        value: "name",
                      })}
                      value={formData.designation}
                      onChange={(val) => onValueChange(val, "designation")}
                      placeholder="Select or Enter Job Title"
                      isCreatable={true}
                      sortOptionsAlphabetically
                      isLoading={apiLoading}
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="location" className="form-label">
                      Your Location *
                    </Label>
                    <SearchableDropdown
                      placeholder="Select location"
                      options={getDropdownOptions(jobLocations)}
                      value={formData.location}
                      sortOptionsAlphabetically
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          location: value?.toString(),
                        }))
                      }
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="mb-3">
                    <Label
                      htmlFor="total_work_experience"
                      className="form-label"
                    >
                      Total Work Experience *
                    </Label>
                    <SearchableDropdown
                      placeholder="Select work experience"
                      options={workExperienceList?.map((exp) => ({
                        label: `${getWorkExpText(exp)}`,
                        value: `${exp}`,
                      }))}
                      value={`${formData.total_work_experience || ""}`}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          total_work_experience: value?.toString(),
                        }))
                      }
                    />
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="job-categories" className="form-label">
                      Job Industry
                    </Label>
                    <Input
                      type="select"
                      className="form-select"
                      id="job-categories"
                      name="jobInduestry"
                      value={formData.jobInduestry}
                      onChange={(e) => onValueChange(e, "jobInduestry")}
                    >
                      <option value="">--select--</option>
                      {jobTypes.map((type) => (
                        <option
                          key={type.id || type.jobInduestry}
                          value={type.id || type.jobInduestry}
                        >
                          {type.name || type.industryName || type.title}
                        </option>
                      ))}
                    </Input>
                  </div>
                </Col>

                {/* Current Salary — free text, number only */}
                <Col lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="currentSalaryStr" className="form-label">
                      Current Salary Per Month *
                    </Label>
                    <Input
                      type="text"
                      id="currentSalaryStr"
                      placeholder="Enter current salary"
                      value={formData.currentSalaryStr}
                      onChange={(e) =>
                        onNumberInputChange(e, "currentSalaryStr")
                      }
                    />
                  </div>
                </Col>

                {/* Expected Salary — dropdown */}
                <Col lg={6}>
                  <div className="mb-3">
                    <Label htmlFor="expectedSalaryStr" className="form-label">
                      Expected Salary Per Month *
                    </Label>
                    <Input
                      type="select"
                      className="form-select"
                      id="expectedSalaryStr"
                      name="expectedSalaryStr"
                      value={formData.expectedSalaryStr}
                      onChange={(e) =>
                        onValueChange(e, "expectedSalaryStr")
                      }
                    >
                      <option value="">Select Expected Salary</option>
                      {salaryOptions.map((salary, index) => (
                        <option key={index} value={salary}>
                          {salary}
                        </option>
                      ))}
                    </Input>
                  </div>
                </Col>

                <Col lg={12}>
                  <div className="mb-3">
                    <Label htmlFor="resumeFile" className="form-label">
                      Resume *
                    </Label>
                    <Input
                      type="file"
                      id="resumeFile"
                      accept=".pdf,.doc,.docx"
                      onChange={onFileChange}
                    />
                    {formData.resumeFile && (
                      <div className="mt-2 text-success small fw-medium d-flex align-items-center">
                        <i className="uil uil-check-circle me-1 fs-15"></i>
                        <span>
                          <strong>Uploaded: </strong>
                          {formData.resumeFile}
                        </span>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

     <div className="mt-4">
  <button
    type="submit"
    className="btn btn-primary w-100 p-2 fs-16 fw-bold"
    disabled={!isFormValid || loading}
  >
    {loading ? (
      <>
        Saving <Spinner size="sm" className="ms-2" />
      </>
    ) : (
      "Continue"
    )}
  </button>
</div>
            </Form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default CompulsaryCandidateProfile;